/* eslint-disable react/jsx-props-no-spreading */
import React, {
    useMemo,
    useRef,
    useCallback,
    KeyboardEvent,
    Dispatch,
    SetStateAction,
} from 'react';
import {
    useTable,
    useSortBy,
    useBlockLayout,
    Cell,
    Row,
    HeaderGroup,
} from 'react-table';
import { FixedSizeList } from 'react-window';
import { useAutoSize } from '@trqk/hooks';
import { LogCell } from '../..';

const rowHeight = 33;
const minWidthOffset = 40;

interface LogsTableArgs {
    logRows: LogEvent[];
    selected?: Row<LogEvent> | null;
    setSelected?: Dispatch<SetStateAction<Row<LogEvent> | null>>;
}

export const LogsTable = ({
    logRows,
    selected,
    setSelected,
}: LogsTableArgs): JSX.Element => {
    const data = useMemo(
        () => logRows.filter((element: LogEvent) => element.isValid),
        [logRows],
    );

    const tableElementRef = useRef<HTMLTableElement>(null);
    const { height } = useAutoSize({
        elementRef: tableElementRef,
        resizeRate: 400,
        bottomOffset: 52,
        minWidth: 1038 - minWidthOffset,
    });

    const columns = useMemo(
        () => [
            {
                Header: 'User ID',
                accessor: ({ userId }: LogEvent): string => userId,
                width: 300,
                Cell: LogCell,
            },
            {
                Header: 'Date',
                accessor: ({ date }: LogEvent): string => date,
                width: 200,
                Cell: LogCell,
                sortType: (
                    rowA: Row<LogEvent>,
                    rowB: Row<LogEvent>,
                ): number => {
                    // Dates in the future will always be sorted to the top of the list,
                    // so if a row's date is undefined, the placeholder value
                    // of tomorrow makes sure it will be easy to spot
                    const tomorrow = new Date();

                    tomorrow.setDate(new Date().getDate() + 1);

                    const dateA = rowA?.original?.dateData ?? tomorrow;
                    const dateB = rowB?.original?.dateData ?? tomorrow;

                    if (dateA < dateB) {
                        return -1;
                    }
                    if (dateA > dateB) {
                        return 1;
                    }

                    return 0;
                },
            },
            {
                Header: 'Event Type',
                accessor: ({ eventType }: LogEvent): string => eventType,
                width: 250,
                Cell: LogCell,
            },
            {
                Header: 'Status',
                accessor: ({ eventStatus }: LogEvent): string => eventStatus,
                width: 100,
                Cell: LogCell,
            },
        ],
        [],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        totalColumnsWidth,
    } = useTable(
        {
            columns,
            data,
            autoResetSortBy: false,
            initialState: {
                sortBy: [
                    {
                        id: 'Date',
                        desc: true,
                    },
                ],
            },
        },
        useSortBy,
        useBlockLayout,
    );
    const RenderRow = useCallback(
        ({ index, style }) => {
            const row = rows[index];

            prepareRow(row);

            const { key: rowKey, ...rowRest } = row.getRowProps();

            return (
                <div
                    key={rowKey}
                    {...rowRest}
                    onClick={(): void => setSelected && setSelected(row)}
                    onKeyPress={(e: KeyboardEvent): void => {
                        if (e.key === 'Enter' && setSelected) {
                            setSelected(row);
                        }
                    }}
                    style={style}
                    role="row"
                    className="outline-none"
                    tabIndex={0}
                >
                    {row.cells.map((cell: Cell<LogEvent>) => {
                        const rowSelected =
                            row.original.key === selected?.original.key;

                        const { key: cellKey, ...cellRest } =
                            cell.getCellProps();

                        return (
                            <div
                                key={cellKey}
                                {...cellRest}
                                className={`p-1 border border-tertiary-400
                                     ${rowSelected ? 'bg-secondary-100' : ''}
                                    `}
                            >
                                {cell.render('Cell')}
                            </div>
                        );
                    })}
                </div>
            );
        },
        [prepareRow, rows, selected],
    );

    return (
        <div {...getTableProps()} ref={tableElementRef}>
            <div>
                {headerGroups.map((headerGroup: HeaderGroup<LogEvent>) => {
                    const { key: headerGroupKey, ...headerGroupRest } =
                        headerGroup.getHeaderGroupProps();

                    return (
                        <div key={headerGroupKey} {...headerGroupRest}>
                            {headerGroup.headers.map(column => {
                                const { key: headerKey, ...headerRest } =
                                    column.getHeaderProps(
                                        column.getSortByToggleProps(),
                                    );

                                return (
                                    <div
                                        key={headerKey}
                                        className="border bg-primary-100 font-bold text-left"
                                        {...headerRest}
                                    >
                                        {column.render('Header')}
                                        <span>
                                            {((): string => {
                                                if (column.isSorted) {
                                                    if (column.isSortedDesc) {
                                                        return '▼';
                                                    }

                                                    return '▲';
                                                }

                                                return '';
                                            })()}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
            <div {...getTableBodyProps()}>
                {rows.length > 0 && (
                    <FixedSizeList
                        height={height}
                        itemCount={rows.length}
                        itemSize={rowHeight}
                        width={totalColumnsWidth + 20}
                    >
                        {RenderRow}
                    </FixedSizeList>
                )}
            </div>
        </div>
    );
};
